import { store } from 'redux/store';
import { useHistory } from 'react-router-dom';
import { FC, ReactNode, createContext, useContext, useState } from 'react';
import { useTranslate } from 'hooks/useTranslate';

import successSwal from 'utils/successSwal';
import errorSwal from 'utils/errorSwal';

import actionTypes from 'constants/actionTypes';

import { TradingOffer } from 'types/TradingOffer';
import { setModal } from 'modules/_shared/redux/modalActions';
import { addTradingOffer } from 'modules/trading/redux/tradingOfferActions';

import { PublishTradingOfferModalContextType } from '../types/PublishTradingOfferModalContextType';
import { publishTradingOfferModalInitValue } from '../constants/publishTradingOfferModalInitValue';
import { useGetValidityDays } from '../hooks/useGetValidityDates';

export const PublishTradingOfferModalContext =
  createContext<PublishTradingOfferModalContextType>(
    publishTradingOfferModalInitValue
  );

export const PublishTradingOfferModalProvider: FC<{
  children: ReactNode;
  tradingOffer: Partial<TradingOffer> & { className: string };
  modalActionType?: string;
}> = ({ children, tradingOffer, modalActionType = actionTypes.ADD }) => {
  const { t } = useTranslate();
  const history = useHistory();

  const [isTermsAndConditionsAccepted, setIsTermsAndConditionsAccepted] =
    useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { data: validityDaysData } = useGetValidityDays({
    startDate: tradingOffer.startDate,
    endDate: tradingOffer.endDate,
  });

  const handleCloseModal = () => {
    store.dispatch(setModal(null));
  };

  const handleChangeTermsAndConditionsAcceptance = () => {
    setIsTermsAndConditionsAccepted((prev) => !prev);
  };

  const handlePublishTradingOffer = async () => {
    try {
      setIsLoading(true);

      await store.dispatch(addTradingOffer(tradingOffer));

      setIsLoading(false);

      const successMessage =
        modalActionType === actionTypes.EDIT
          ? t('OfferUpdatedSuccessfully')
          : t('OfferPublishedSuccessfully');

      successSwal(successMessage);
      handleCloseModal();

      history.push(`/ofertas/${tradingOffer.society}`);
    } catch (error) {
      setIsLoading(false);
      const errorMessage =
        modalActionType === actionTypes.EDIT
          ? t('ErrorUpdatingOffer')
          : t('ErrorPublishingTradingOffer');

      errorSwal(errorMessage);
      handleCloseModal();
    }
  };

  return (
    <PublishTradingOfferModalContext.Provider
      value={{
        data: {
          modalActionType,
          tradingOffer,
          validityDays: validityDaysData.validityDays,
          isTermsAndConditionsAccepted,
          isLoading,
        },
        actions: {
          handleCloseModal,
          handleChangeTermsAndConditionsAcceptance,
          handlePublishTradingOffer,
        },
      }}
    >
      {children}
    </PublishTradingOfferModalContext.Provider>
  );
};

export const usePublishTradingOfferModal = () => {
  const context = useContext(PublishTradingOfferModalContext);
  if (!context) {
    throw new Error(
      'usePublishTradingOfferModal must be used within a PublishTradingOfferModalProvider'
    );
  }
  return context;
};
